import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Admin,
  Analytics,
  Managment,
  Monitoring,
  Operations,
  ControlPanelIcon,
  MonitorIcon,
  ActivityMap,
  IncidentReportIcon,
  ActivityLog,
  WorkHistory,
  TimesheetIcon,
  GroupIcon,
  SitesIcon,
  ClientsIcon,
  MobileUnitsIcon,
  SecurityIcon,
  PaperIcon,
  ControlPanel,
  InfoIcon,
  UserManualIcon,
  SettingsWhilsIcon,
  PerformanceIcon,
  InfoboardIcon,
  FormIcon,
  ContactIcon,
  CheckboxChecked,
  TourIcon,
  DatabaseIcon,
  VisitorsIcon,
  PropertyIcon,
  VisitorEntranceExitIcon,
  BranchIcon,
  ReportsIcon,
  NoteIcon,
} from 'assets/icons';
import IntlMessages, { useIntl } from 'util/IntlMessages';
import { BASE_APP_PATH } from 'new-design/routes/common/constants';
import { usePageId } from 'new-design/hooks/usePageId';
import { formatFullName } from 'util/formatting';
import { NEW } from 'app/types/constants';
import * as auth from 'ducks/auth';
import { UserTypes } from 'ducks/roles/types';
import { MenuPermissions } from 'ducks/auth/types';

export const useMenuItems = () => {
  const { hashId } = usePageId();
  const { formatMessage } = useIntl();

  const { userInfo, companyInfo } = useSelector(auth.selectors);
  const hasPermission = useSelector(auth.hasPermission);

  const menuItems = useMemo(() => {
    switch (userInfo.userType) {
      case UserTypes.EMPLOYEE:
      case UserTypes.ADMINISTRATOR:
        return [
          {
            name: <IntlMessages id="topMenu_monitoring" />,
            type: 'collapse',
            icon: <Monitoring />,
            children: [
              {
                name: <IntlMessages id="topMenu_monitoring_control_panel" />,
                type: 'item',
                link: `${BASE_APP_PATH}/control-panel`,
                icon: <ControlPanelIcon />,
                permission: MenuPermissions.CONTROL_PANEL,
              },
              {
                name: <IntlMessages id="topMenu_monitoring_activity_monitoring" />,
                type: 'item',
                link: `${BASE_APP_PATH}/activity-monitor`,
                icon: <MonitorIcon />,
                permission: MenuPermissions.ACTIVITY_MONITOR,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/shift-started`,
                    name: <IntlMessages id="cp.shift.shift_started" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/shift-end`,
                    name: <IntlMessages id="cp.shift.shift_ended" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/checkpoint-scanned`,
                    name: <IntlMessages id="checkpoint_scanned" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/task-completed`,
                    name: <IntlMessages id="task_completed" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/note-created/:id`,
                    name: <IntlMessages id="note_created" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/info-note-created`,
                    name: <IntlMessages id="info_note_created" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/info-note-ack`,
                    name: <IntlMessages id="info_note_ack" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/tour`,
                    name: <IntlMessages id="tour" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/visit`,
                    name: <IntlMessages id="site_visit" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/gps-alert`,
                    name: <IntlMessages id="gps_alert" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/no-activity`,
                    name: <IntlMessages id="no_activity" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-monitor/alarm`,
                    name: <IntlMessages id="alarm" />,
                  },
                ],
              },
              {
                name: <IntlMessages id="topMenu_monitoring_activity_map" />,
                type: 'item',
                link: `${BASE_APP_PATH}/activity-map`,
                icon: <ActivityMap />,
                permission: MenuPermissions.ACTIVITY_MAP,
              },
            ],
          },
          {
            name: <IntlMessages id="topMenu_operations" />,
            type: 'collapse',
            icon: <Operations />,
            children: [
              {
                name: <IntlMessages id="topMenu_analytics_activity_log" />,
                type: 'item',
                link: `${BASE_APP_PATH}/activity-log`,
                icon: <ActivityLog />,
                permission: MenuPermissions.ACTIVITY_LOG,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/activity-log/shift-started`,
                    name: <IntlMessages id="cp.shift.shift_started" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/shift-end`,
                    name: <IntlMessages id="cp.shift.shift_ended" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/checkpoint-scanned`,
                    name: <IntlMessages id="checkpoint_scanned" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/task-completed`,
                    name: <IntlMessages id="task_completed" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/note-created/:id`,
                    name: <IntlMessages id="note_created" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/info-note-created`,
                    name: <IntlMessages id="info_note_created" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/info-note-ack`,
                    name: <IntlMessages id="info_note_ack" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/tour`,
                    name: <IntlMessages id="tour" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/visit`,
                    name: <IntlMessages id="site_visit" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/gps-alert`,
                    name: <IntlMessages id="gps_alert" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/no-activity`,
                    name: <IntlMessages id="no_activity" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/activity-log/alarm`,
                    name: <IntlMessages id="alarm" />,
                  },
                ],
              },
              {
                name: <IntlMessages id="forms" />,
                type: 'item',
                link: `${BASE_APP_PATH}/form-results`,
                icon: <FormIcon />,
                permission: MenuPermissions.FORM_RESULTS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/form-results/:id/fill`,
                    name: <IntlMessages id="fill_form" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/form-results/:id/view`,
                    name: <IntlMessages id="view_form" />,
                  },
                ],
              },
              {
                name: <IntlMessages id="topMenu_operations_info_board" />,
                type: 'item',
                link: `${BASE_APP_PATH}/info-board`,
                icon: <InfoboardIcon />,
                permission: MenuPermissions.INFO_BOARD,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/info-board/:id`,
                    name: <IntlMessages id="info_note_edit" />,
                  },
                ],
              },
              {
                name: <IntlMessages id="incidents" />,
                type: 'item',
                link: `${BASE_APP_PATH}/incident-reports`,
                historyBack: true,
                icon: <IncidentReportIcon />,
                permission: MenuPermissions.INCIDENT_REPORTS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/incident-reports/new`,
                    name: <IntlMessages id="new_incident_reports" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/incident-reports/:id`,
                    name: <IntlMessages id="edit_incident_reports" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/incident-reports/:id/view`,
                    name: <IntlMessages id="view_incident_reports" />,
                  },
                ],
              },
              {
                name: <IntlMessages id="notes" />,
                type: 'item',
                link: `${BASE_APP_PATH}/notebook-notes`,
                historyBack: true,
                icon: <NoteIcon />,
                permission: MenuPermissions.NOTEBOOK_NOTES,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/notebook-notes/:id`,
                    name: <IntlMessages id="view_note" />,
                  },
                ],
              },
              {
                name: <IntlMessages id="visitor_log" />,
                type: 'item',
                link: `${BASE_APP_PATH}/visitor-log`,
                icon: <VisitorsIcon />,
                permission: MenuPermissions.VISITOR_LOG,
                isVisible: ({ visitorLogEnabled }) => visitorLogEnabled,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/visitor-log/register`,
                    name: <IntlMessages id="register_visit" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/visitor-log/:id`,
                    name: ({ params: { id }, state: { visitorLogInfo } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return visitorLogInfo[+id]?.eventType
                        ? formatMessage({ id: visitorLogInfo[+id]?.eventType })
                        : `#${id}`;
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="reports" />,
                type: 'item',
                link: `${BASE_APP_PATH}/pdf-reports`,
                icon: <ReportsIcon />,
                permission: MenuPermissions.PDF_REPORTS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/pdf-reports/:entity`,
                    historyBack: true,
                    name: ({ params: { entity } }) => {
                      return <IntlMessages id={entity} />;
                    },
                  },
                ],
              },
            ],
          },
          {
            name: <IntlMessages id="topMenu_analytics" />,
            type: 'collapse',
            icon: <Analytics />,
            children: [
              {
                name: <IntlMessages id="bi_and_statistics" />,
                type: 'item',
                link: `${BASE_APP_PATH}/bi`,
                icon: <PropertyIcon />,
                permission: MenuPermissions.BI_AND_STATISTICS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/bi/tours-detailed`,
                    name: <IntlMessages id="tours" />,
                    subpages: [
                      {
                        link: `${BASE_APP_PATH}/bi/tours-detailed/by-sites/:siteID`,
                        historyBack: true,
                        name: ({ params: { siteID }, state: { sitesList } }) => {
                          return sitesList?.find((el) => el.siteID === +siteID)?.siteName || `#${siteID}`;
                        },
                        subpages: [
                          {
                            link: `${BASE_APP_PATH}/bi/tours-detailed/by-sites/:id/tour`,
                            name: <IntlMessages id="cp.tour.tour_details" />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    link: `${BASE_APP_PATH}/bi/incident-reports-detailed`,
                    name: <IntlMessages id="incident_reports" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/bi/notes-detailed`,
                    name: <IntlMessages id="notes" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/bi/site-visits`,
                    name: <IntlMessages id="site_visits" />,
                    subpages: [
                      {
                        link: `${BASE_APP_PATH}/bi/site-visits/visit`,
                        name: <IntlMessages id="visit_details" />,
                      },
                    ],
                  },
                ],
              },
              {
                name: <IntlMessages id="topMenu_analytics_performance" />,
                type: 'item',
                link: `${BASE_APP_PATH}/performance`,
                icon: <PerformanceIcon />,
                permission: MenuPermissions.PERFORMANCE,
              },
              {
                name: <IntlMessages id="topMenu_analytics_work_history" />,
                type: 'item',
                link: `${BASE_APP_PATH}/work-history`,
                icon: <WorkHistory />,
                permission: MenuPermissions.WORK_HISTORY,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/work-history/shift#shiftId=${hashId}`,
                    name: <IntlMessages id="cp.shift.shift_details" />,
                    subpages: [
                      {
                        link: `${BASE_APP_PATH}/work-history/shift/logs#id=${hashId}`,
                        name: <IntlMessages id="shift_log" />,
                      },
                    ],
                  },
                ],
              },
              {
                name: <IntlMessages id="topMenu_analytics_timesheets" />,
                type: 'item',
                link: `${BASE_APP_PATH}/timesheets`,
                icon: <TimesheetIcon />,
                permission: MenuPermissions.TIMESHEETS,
              },
            ],
          },
          {
            name: <IntlMessages id="topMenu_managment" />,
            type: 'collapse',
            icon: <Managment />,
            children: [
              {
                name: <IntlMessages id="clients" />,
                type: 'item',
                link: `${BASE_APP_PATH}/clients`,
                icon: <ClientsIcon />,
                permission: MenuPermissions.CLIENTS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/clients/:id`,
                    historyBack: true,
                    name: ({ params: { id }, state: { clientsList } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return clientsList.find((el) => el.clientID === +id)?.clientName || `#${id}`;
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="contacts" />,
                type: 'item',
                link: `${BASE_APP_PATH}/contacts`,
                icon: <ContactIcon />,
                permission: MenuPermissions.CONTACTS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/contacts/:id`,
                    name: ({ params: { id }, state: { contactInfo } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return (
                        formatFullName(contactInfo[+id]?.firstName, contactInfo[+id]?.lastName) || `#${id}`
                      );
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="sites" />,
                type: 'item',
                link: `${BASE_APP_PATH}/sites`,
                icon: <SitesIcon />,
                permission: MenuPermissions.SITES,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/sites/:id`,
                    historyBack: true,
                    name: ({ params: { id }, state: { sitesList } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return sitesList?.find((el) => el.siteID === +id)?.siteName || `#${id}`;
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="tasks" />,
                type: 'item',
                link: `${BASE_APP_PATH}/tasks`,
                icon: <CheckboxChecked />,
                permission: MenuPermissions.TASKS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/tasks/:id`,
                    name: ({ params: { id }, state: { taskInfo } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return taskInfo[+id]?.title || `#${id}`;
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="tours" />,
                type: 'item',
                link: `${BASE_APP_PATH}/tours`,
                icon: <TourIcon />,
                permission: MenuPermissions.TOURS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/tours/:id`,
                    name: ({ params: { id }, state: { tourInfo } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return tourInfo[+id]?.tourName || `#${id}`;
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="mobile_units" />,
                type: 'item',
                link: `${BASE_APP_PATH}/mobile-units`,
                icon: <MobileUnitsIcon />,
                permission: MenuPermissions.MOBILE_UNITS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/mobile-units/:id`,
                    name: ({ params: { id }, state: { mobilePatrolsList } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return mobilePatrolsList?.find((el) => el.mobilePatrolID === +id)?.name || `#${id}`;
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="administrators" />,
                type: 'item',
                link: `${BASE_APP_PATH}/administrators`,
                icon: <GroupIcon />,
                permission: MenuPermissions.ADMINISTRATORS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/administrators/new`,
                    name: <IntlMessages id="new" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/administrators/:id`,
                    name: ({ params: { id }, state: { administratorInfo } }) =>
                      administratorInfo[+id]
                        ? formatFullName(administratorInfo[+id].firstName, administratorInfo[+id].lastName)
                        : `#${id}`,
                  },
                ],
              },
              {
                name: <IntlMessages id="cp.dashboard.employees" />,
                type: 'item',
                link: `${BASE_APP_PATH}/employees`,
                icon: <GroupIcon />,
                permission: MenuPermissions.EMPLOYEES,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/employees/new`,
                    name: <IntlMessages id="new" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/employees/:id`,
                    historyBack: true,
                    name: ({ params: { id }, state: { employeeInfo } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      const employee = employeeInfo[+id];
                      return employee ? formatFullName(employee.firstName, employee.lastName) : `#${id}`;
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="visitors" />,
                type: 'item',
                link: `${BASE_APP_PATH}/visitors`,
                icon: <VisitorsIcon />,
                permission: MenuPermissions.VISITORS,
                isVisible: ({ visitorLogEnabled }) => visitorLogEnabled,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/visitors/:id`,
                    name: ({ params: { id }, state: { visitorsInfo } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      const { firstName, lastName } = visitorsInfo[+id] || {};
                      return visitorsInfo[+id] ? `${firstName} ${lastName}` : `#${id}`;
                    },
                  },
                ],
              },
            ],
          },
          {
            name: <IntlMessages id="topMenu_admin" />,
            type: 'collapse',
            icon: <Admin />,
            children: [
              {
                name: <IntlMessages id="my_account" />,
                type: 'item',
                link: `${BASE_APP_PATH}/my-account`,
                icon: <SecurityIcon />,
                permission: MenuPermissions.ACCOUNT_INFO,
              },
              {
                name: <IntlMessages id="branches" />,
                type: 'item',
                link: `${BASE_APP_PATH}/branches`,
                icon: <BranchIcon />,
                permission: MenuPermissions.BRANCHES,
                isVisible: ({ branchesEnabled }) => branchesEnabled,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/branches/new`,
                    name: <IntlMessages id="new" />,
                  },
                  {
                    link: `${BASE_APP_PATH}/branches/:id`,
                    name: ({ params: { id }, state: { branchInfo } }) => branchInfo[+id]?.name || `#${id}`,
                  },
                ],
              },
              {
                name: <IntlMessages id="forms" />,
                type: 'item',
                link: `${BASE_APP_PATH}/forms`,
                icon: <FormIcon />,
                permission: MenuPermissions.FORMS,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/forms/:id`,
                    name: ({ params: { id }, state: { formInfo } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return formInfo[+id]?.name || `#${id}`;
                    },
                  },
                ],
              },
              {
                name: <IntlMessages id="settings" />,
                type: 'item',
                link: `${BASE_APP_PATH}/settings`,
                icon: <SettingsWhilsIcon />,
                permission: MenuPermissions.SETTINGS,
              },
              {
                name: <IntlMessages id="system_log" />,
                type: 'item',
                link: `${BASE_APP_PATH}/system-log`,
                icon: <DatabaseIcon />,
                permission: MenuPermissions.SYSTEM_LOG,
                subpages: [
                  {
                    link: `${BASE_APP_PATH}/system-log/:id`,
                    name: ({ params: { id }, state: { auditLogInfo } }) => {
                      if (id === NEW) return formatMessage({ id: 'new' });

                      return (
                        auditLogInfo[+id]?.eventID &&
                        `${formatMessage({ id: 'event_id' })} - #${auditLogInfo[+id]?.eventID}`
                      );
                    },
                  },
                ],
              },
            ],
          },
          {
            name: <IntlMessages id="information" />,
            type: 'collapse',
            icon: <InfoIcon />,
            children: [
              {
                name: <IntlMessages id="user_manual" />,
                type: 'item',
                link: `${BASE_APP_PATH}/user-manual`,
                icon: <UserManualIcon />,
              },
              {
                name: <IntlMessages id="cp.footer.terms" />,
                type: 'item',
                link: `${BASE_APP_PATH}/terms`,
                icon: <PaperIcon />,
              },
              {
                name: <IntlMessages id="cp.footer.policy" />,
                type: 'item',
                link: `${BASE_APP_PATH}/policy`,
                icon: <PaperIcon />,
              },
            ],
          },
        ];

      case UserTypes.CONTACT:
        return [
          {
            name: <IntlMessages id="cp.dashboard.dashboard" />,
            type: 'item',
            link: `${BASE_APP_PATH}/control-panel`,
            icon: <ControlPanel />,
            permission: MenuPermissions.CONTROL_PANEL,
            children: [],
          },
          {
            name: <IntlMessages id="topMenu_analytics_activity_log" />,
            type: 'item',
            icon: <ActivityLog />,
            link: `${BASE_APP_PATH}/activity-log`,
            permission: MenuPermissions.ACTIVITY_LOG,
            children: [],
          },
          {
            name: <IntlMessages id="incidents" />,
            type: 'item',
            link: `${BASE_APP_PATH}/incident-reports`,
            icon: <IncidentReportIcon />,
            permission: MenuPermissions.INCIDENT_REPORTS,
            children: [],
          },
          {
            name: <IntlMessages id="notes" />,
            type: 'item',
            link: `${BASE_APP_PATH}/notebook-notes`,
            icon: <NoteIcon />,
            permission: MenuPermissions.NOTEBOOK_NOTES,
            children: [],
          },
          {
            name: <IntlMessages id="forms" />,
            type: 'item',
            link: `${BASE_APP_PATH}/form-results`,
            icon: <FormIcon />,
            permission: MenuPermissions.FORM_RESULTS,
            children: [],
          },
          {
            name: <IntlMessages id="reports" />,
            type: 'item',
            link: `${BASE_APP_PATH}/pdf-reports`,
            icon: <ReportsIcon />,
            permission: MenuPermissions.PDF_REPORTS,
            children: [
              {
                link: `${BASE_APP_PATH}/pdf-reports/:entity`,
                historyBack: true,
                name: ({ params: { entity } }) => {
                  return <IntlMessages id={entity} />;
                },
              },
            ],
          },
          {
            name: <IntlMessages id="visitor_log" />,
            type: 'collapse',
            icon: <VisitorsIcon />,
            permission: MenuPermissions.VISITOR_LOG,
            isVisible: ({ visitorLogEnabled }) => visitorLogEnabled,
            children: [
              {
                name: <IntlMessages id="visitors" />,
                type: 'item',
                link: `${BASE_APP_PATH}/visitors`,
                icon: <VisitorsIcon />,
                permission: MenuPermissions.VISITORS,
              },
              {
                name: <IntlMessages id="visits" />,
                type: 'item',
                link: `${BASE_APP_PATH}/visitor-log`,
                icon: <VisitorEntranceExitIcon />,
                permission: MenuPermissions.VISITOR_LOG,
              },
            ],
          },
        ];

      default:
        return [];
    }
  }, [userInfo.userType]);

  return useMemo(() => {
    return menuItems
      .map((group) => {
        const children = (group.children || []).filter(
          ({ permission, isVisible }) =>
            (!permission || hasPermission(permission)) &&
            (!isVisible ||
              isVisible({
                visitorLogEnabled: companyInfo.visitorLogEnabled,
                branchesEnabled: companyInfo.branchesEnabled,
              }))
        );

        return {
          ...group,
          children,
        };
      })
      .filter((group) => group.type === 'item' || group.children.length);
  }, [companyInfo]);
};
